import { useState, useEffect } from "react";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import type { SanityArticleSummary, SanityCategoryWithCount } from "@/lib/sanity.types";
import BlogCard from "@/components/blog/BlogCard";
import Pagination from "@/components/blog/Pagination";
import { Search, X, SearchX } from "lucide-react";

type BlogListProps = {
  articles: SanityArticleSummary[];
  pagination: {
    page: number;
    pageSize: number;
    pageCount: number;
    total: number;
  };
  categories?: SanityCategoryWithCount[];
  activeCategory?: string;
};

export default function BlogList({ articles, pagination, categories, activeCategory }: BlogListProps) {
  const navigate = useNavigate({ from: "/blog" });
  const search: any = useSearch({ from: "/blog", strict: false });
  const initialQuery = (search?.q as string) || "";

  const [value, setValue] = useState(initialQuery);

  useEffect(() => {
    setValue(initialQuery);
  }, [initialQuery]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    navigate({ search: (prev: any) => ({ ...prev, q: value || undefined, page: 1 }) });
  };

  const clearSearch = () => {
    setValue("");
    navigate({ search: (prev: any) => ({ ...prev, q: undefined, page: 1 }) });
  };

  const safeArticles = Array.isArray(articles) ? articles : [];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 sm:pt-36 pb-16">
      {/* Hero Header */}
      <header className="relative mb-14 text-center">
        <h1 className="text-balance text-4xl font-bold tracking-tight sm:text-5xl lg:text-[3.35rem] lg:leading-[1.08] text-foreground mb-4">
          <span className="gradient-text">Zapify</span> Blog
        </h1>
        <p className="mx-auto max-w-[46ch] text-[15px] leading-relaxed text-muted-foreground sm:text-[17px]">
          Expert insights on Instagram automation, AI-powered engagement, and social media growth strategies.
        </p>

        {/* Search Bar */}
        <div className="max-w-md mx-auto mt-8">
          <form onSubmit={handleSearch} className="relative flex items-center">
            <div className="absolute left-4 text-muted-foreground pointer-events-none">
              <Search className="w-4 h-4" />
            </div>
            <input
              type="text"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="Search articles..."
              className="w-full bg-background border border-border rounded-full py-3 pl-11 pr-12 text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring/40 focus:border-ring text-sm transition-all"
            />
            {value && (
              <button
                type="button"
                onClick={clearSearch}
                className="absolute right-14 p-1 text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Clear search"
              >
                <X className="w-4 h-4" />
              </button>
            )}
            <button
              type="submit"
              className="absolute right-1.5 h-[34px] px-4 rounded-full bg-primary-gradient text-primary-foreground text-sm font-semibold hover:brightness-[0.97] active:brightness-[0.92] transition-all shadow-sm"
            >
              Search
            </button>
          </form>
        </div>

        {/* Active search indicator */}
        {initialQuery && (
          <div className="mt-4 flex items-center justify-center gap-2 text-sm text-muted-foreground">
            <span>
              Results for "<span className="font-semibold text-foreground">{initialQuery}</span>"
            </span>
            <button
              type="button"
              onClick={clearSearch}
              className="gradient-text font-medium hover:opacity-80"
            >
              Clear
            </button>
          </div>
        )}
      </header>

      {/* Category Filter Pills */}
      {categories && categories.length > 0 && (
        <div className="mb-10 flex flex-wrap justify-center gap-2 sm:gap-2.5">
          <Link
            to="/blog"
            search={{ page: 1 }}
            className={`rounded-full px-4 py-2 text-sm font-medium transition-all border ${
              !activeCategory
                ? "bg-primary-gradient text-primary-foreground border-transparent shadow-sm shadow-primary/20"
                : "bg-background text-foreground border-border hover:border-ring/40 hover:bg-secondary"
            }`}
          >
            All Posts ({pagination.total})
          </Link>
          {categories.map((cat) => (
            <Link
              key={cat.id}
              to="/blog"
              search={{ page: 1, category: cat.slug }}
              className={`rounded-full px-4 py-2 text-sm font-medium transition-all border ${
                activeCategory === cat.slug
                  ? "bg-primary-gradient text-primary-foreground border-transparent shadow-sm shadow-primary/20"
                  : "bg-background text-foreground border-border hover:border-ring/40 hover:bg-secondary"
              }`}
            >
              {cat.name} ({cat.count})
            </Link>
          ))}
        </div>
      )}

      {/* Blog Cards Grid / Empty State */}
      {safeArticles.length === 0 ? (
        <div className="text-center py-24 px-4">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-secondary rounded-full mb-6">
            <SearchX className="w-8 h-8 text-muted-foreground" />
          </div>
          <h3 className="text-xl font-bold text-foreground mb-2">
            No articles found
          </h3>
          <p className="text-muted-foreground max-w-sm mx-auto mb-8">
            Try adjusting your search or browse all categories.
          </p>
          <Link
            to="/blog"
            search={{ page: 1 }}
            className="inline-flex items-center justify-center px-6 py-2.5 rounded-full bg-primary-gradient text-primary-foreground text-sm font-semibold hover:brightness-[0.97] transition-all shadow-sm shadow-primary/20"
          >
            View All Posts
          </Link>
        </div>
      ) : (
        <>
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {safeArticles.map((post) => (
              <BlogCard key={post.id} post={post} />
            ))}
          </div>

          {/* Pagination */}
          <div className="mt-12">
            <Pagination
              currentPage={pagination.page}
              pageCount={pagination.pageCount}
              pageSize={pagination.pageSize}
              total={pagination.total}
            />
          </div>
        </>
      )}
    </div>
  );
}
